import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrayingHands, faCalendarAlt, faUsers, faComment } from '@fortawesome/free-solid-svg-icons';

function SchoolPrayerRequestsTab() {
    const { id: schoolId } = useParams();
    const [data, setData] = useState({
        prayerRequests: [],
        current_page: 1,
        last_page: 1,
        per_page: 5,
        total: 0
    });
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [sortOrder, setSortOrder] = useState('desc');
    const navigate = useNavigate();
    const location = useLocation();

    const [filters, setFilters] = useState({
        page: 1,
        per_page: 5
    });

    useEffect(() => {
        const fetchPrayerRequests = async () => {
            try {
                const token = localStorage.getItem("access_token");
                const response = await fetch(
                    `${API_URL}/schools/${schoolId}/prayer-requests?sort=${sortOrder}&page=${filters.page}&per_page=${filters.per_page}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.ok) {
                    const responseData = await response.json();
                    setData({
                        prayerRequests: responseData.data,
                        current_page: responseData.current_page,
                        last_page: responseData.last_page,
                        per_page: responseData.per_page,
                        total: responseData.total
                    });
                } else if (response.status === 404) {
                    setNotification({ type: "info", message: "No prayer requests found for this school." });
                    setData(prev => ({ ...prev, prayerRequests: [] }));
                } else if (response.status === 401) {
                    navigate("/logout", { state: { from: location } });
                } else {
                    throw new Error("Failed to fetch prayer requests");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching prayer requests." });
                setData(prev => ({ ...prev, prayerRequests: [] }));
            } finally {
                setLoading(false);
            }
        };

        fetchPrayerRequests();
    }, [schoolId, filters.page, filters.per_page, sortOrder, navigate, location]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handlePerPageChange = (e) => {
        const perPage = parseInt(e.target.value);
        setFilters(prev => ({
            ...prev,
            per_page: perPage,
            page: 1
        }));
    };

    return (
        <div className="school-prayer-requests">
            {notification.message && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={closeNotification}
                />
            )}

            <div className="columns mb-4">
                <div className="column is-half">
                    <div className="field">
                        <label className="label">Items per page</label>
                        <div className="control select is-fullwidth">
                            <select value={filters.per_page} onChange={handlePerPageChange}>
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="column is-half">
                    <div className="field">
                        <label className="label">Sort by Date</label>
                        <div className="control select is-fullwidth">
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                <option value="desc">Newest First</option>
                                <option value="asc">Oldest First</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="has-text-centered">
                    <p>Loading prayer requests...</p>
                    <progress className="progress is-small is-primary" max="100">15%</progress>
                </div>
            ) : data.prayerRequests.length === 0 ? (
                <div className="has-text-centered">
                    <p className="has-text-grey">No prayer requests found for this school</p>
                </div>
            ) : (
                <>
                    <div className="prayer-requests-list">
                        {data.prayerRequests.map((request) => (
                            <div key={request.id} className="box prayer-request-item">
                                <article className="media">
                                    <div className="media-left">
                                        <span className="icon is-large">
                                            <FontAwesomeIcon icon={faPrayingHands} size="2x" />
                                        </span>
                                    </div>
                                    <div className="media-content">
                                        <div className="content">
                                            <p>
                                                <strong>{request.title || "Prayer Request"} ({new Date(request.mission_date).toLocaleDateString()})  </strong>
                                                <br />
                                                {request.description}
                                            </p>
                                            <div className="tags">
                                                <span className="tag is-info">
                                                    <FontAwesomeIcon icon={faCalendarAlt} /> &nbsp;
                                                    {new Date(request.created_at).toLocaleDateString()}
                                                </span>
                                                {request.mission_name && (
                                                    <span className="tag is-light">
                                                        <FontAwesomeIcon icon={faUsers} /> &nbsp;
                                                        <Link to={`/dashboard/missions/${request.mission_id}/feedback/details`}>
                                                            {request.mission_name}
                                                        </Link>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="media-right">
                                        <Link
                                            to={`/dashboard/missions/${request.mission_id}/feedback/details`}
                                            className="button is-small is-white"
                                            title="View Feedback"
                                        >
                                            <span className="icon">
                                                <FontAwesomeIcon icon={faComment} />
                                            </span>
                                        </Link>
                                    </div>
                                </article>
                            </div>
                        ))}
                    </div>

                    {data.total > 0 && (
                        <nav className="pagination is-centered mt-6" role="navigation" aria-label="pagination">
                            <button
                                className="pagination-previous"
                                onClick={() => handlePageChange(data.current_page - 1)}
                                disabled={data.current_page === 1}
                            >
                                Previous
                            </button>
                            <button
                                className="pagination-next"
                                onClick={() => handlePageChange(data.current_page + 1)}
                                disabled={data.current_page === data.last_page}
                            >
                                Next
                            </button>
                            <ul className="pagination-list">
                                {Array.from({ length: data.last_page }, (_, i) => i + 1).map(page => (
                                    <li key={page}>
                                        <button
                                            className={`pagination-link ${data.current_page === page ? 'is-current' : ''}`}
                                            aria-label={`Go to page ${page}`}
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}

export default SchoolPrayerRequestsTab;